import { useMemo } from "react";
import { CreditCard, Layers, Package, ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { ExportMenu, exportToExcel, exportToPdf, reportFileName, type ExportColumn } from "@/components/reports";
import {
  useStoreBvHistory,
  useStoreCatalog,
  useStoreDashboard,
  useStoreId,
  useStoreOrders,
} from "@/hooks/queries";
import type { StoreOrderRow, StoreProductRow } from "@/types/store.ui";
import type { StoreBvLedgerDto } from "@/types/store.dto";

const paymentLabel = (method: StoreOrderRow["paymentMethod"]) => {
  if (method === "cod") return "COD / Cash";
  if (method === "wallet") return "Wallet";
  return "Razorpay / UPI";
};

const formatBv = (value: number) => (Number.isInteger(value) ? String(value) : Number(value).toFixed(2));
const inr = (value: number) => `₹${value.toLocaleString("en-IN")}`;

function ReportSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-56" />
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );
}

/** Export button wired to one report's rows/columns. */
function ReportExport<T>({
  title,
  name,
  rows,
  columns,
}: {
  title: string;
  name: string;
  rows: T[];
  columns: ExportColumn<T>[];
}) {
  const options = () => ({ title, fileName: reportFileName(name), columns, rows });
  return (
    <ExportMenu
      disabled={rows.length === 0}
      onExcel={() => exportToExcel(options())}
      onPdf={() => exportToPdf(options())}
    />
  );
}

// ---------------------------------------------------------------- Sales

const salesExport: ExportColumn<StoreOrderRow>[] = [
  { header: "Order", value: (r) => r.orderId, width: 22 },
  { header: "Customer", value: (r) => r.customerName, width: 24 },
  { header: "Date", value: (r) => r.orderDate, width: 16 },
  { header: "Payment", value: (r) => paymentLabel(r.paymentMethod), width: 18 },
  { header: "Status", value: (r) => r.status, width: 14 },
  { header: "Items", value: (r) => r.itemCount, numeric: true, width: 8 },
  { header: "Amount (INR)", value: (r) => r.total, numeric: true, width: 14 },
];

export function SalesReport() {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data: dashboard } = useStoreDashboard(storeId);
  const { data, isLoading } = useStoreOrders(storeId, { pageSize: 100 });
  const orders = data?.items ?? [];

  const table = useDataTable<StoreOrderRow>({ data: orders, searchKeys: ["orderId", "customerName"] });

  const columns: DataTableColumn<StoreOrderRow>[] = [
    {
      id: "orderId",
      header: "Order",
      accessorKey: "orderId",
      sortable: true,
      mobilePriority: 1,
      cell: (row) => (
        <div>
          <p className="font-semibold">{row.orderId}</p>
          <p className="text-xs text-muted-foreground">{row.customerName}</p>
        </div>
      ),
    },
    { id: "date", header: "Date", accessorKey: "orderDate", sortable: true, mobileLabel: "Date", mobilePriority: 2 },
    {
      id: "payment",
      header: "Payment",
      mobileLabel: "Payment",
      mobilePriority: 3,
      cell: (row) => <Badge variant="secondary">{paymentLabel(row.paymentMethod)}</Badge>,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      mobileLabel: "Status",
      mobilePriority: 4,
      cell: (row) => <Badge variant="outline" className="capitalize">{row.status}</Badge>,
    },
    { id: "items", header: "Items", accessorKey: "itemCount", align: "right", hideOnMobile: true },
    {
      id: "total",
      header: "Amount",
      accessorKey: "total",
      align: "right",
      sortable: true,
      mobileLabel: "Amount",
      mobilePriority: 5,
      cell: (row) => <span className="font-semibold">{inr(row.total)}</span>,
    },
  ];

  if (storeLoading || isLoading) return <ReportSkeleton />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales Report"
        description="All store orders with payment mode and status (online + POS)."
        actions={<ReportExport title="Sales Report" name="sales-report" rows={table.allRows} columns={salesExport} />}
      />
      <StatsGrid columns={4}>
        <CompactStatCard title="Total orders" value={dashboard?.totalOrders ?? orders.length} icon={ShoppingCart} />
        <CompactStatCard title="Today" value={dashboard?.todayOrders ?? 0} icon={ShoppingCart} />
        <CompactStatCard title="Revenue (delivered)" value={inr(Number(dashboard?.totalRevenue ?? 0))} icon={CreditCard} />
        <CompactStatCard title="Today revenue" value={inr(Number(dashboard?.todayRevenue ?? 0))} icon={CreditCard} />
      </StatsGrid>
      <DataTable
        title="Sales entries"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search order or customer..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No sales yet."
      />
    </div>
  );
}

// ------------------------------------------------------------- Payments

interface PaymentRow {
  method: string;
  orders: number;
  amount: number;
  delivered: number;
}

const paymentsExport: ExportColumn<PaymentRow>[] = [
  { header: "Payment mode", value: (r) => r.method, width: 22 },
  { header: "Orders", value: (r) => r.orders, numeric: true, width: 10 },
  { header: "Delivered", value: (r) => r.delivered, numeric: true, width: 10 },
  { header: "Total amount (INR)", value: (r) => r.amount, numeric: true, width: 18 },
];

export function PaymentsReport() {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data, isLoading } = useStoreOrders(storeId, { pageSize: 200 });
  const orders = data?.items ?? [];

  const rows = useMemo(() => {
    const map = new Map<string, PaymentRow>();
    for (const order of orders) {
      const key = paymentLabel(order.paymentMethod);
      const current = map.get(key) ?? { method: key, orders: 0, amount: 0, delivered: 0 };
      current.orders += 1;
      current.amount += order.total;
      if (order.status === "delivered") current.delivered += 1;
      map.set(key, current);
    }
    return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
  }, [orders]);

  const table = useDataTable<PaymentRow>({ data: rows, searchKeys: ["method"] });

  const columns: DataTableColumn<PaymentRow>[] = [
    { id: "method", header: "Payment mode", accessorKey: "method", sortable: true, mobilePriority: 1 },
    { id: "orders", header: "Orders", accessorKey: "orders", align: "right", sortable: true, mobileLabel: "Orders", mobilePriority: 2 },
    { id: "delivered", header: "Delivered", accessorKey: "delivered", align: "right", mobileLabel: "Delivered", mobilePriority: 3 },
    {
      id: "amount",
      header: "Total amount",
      accessorKey: "amount",
      align: "right",
      sortable: true,
      mobileLabel: "Amount",
      mobilePriority: 4,
      cell: (row) => <span className="font-semibold">{inr(row.amount)}</span>,
    },
  ];

  const totalAmount = rows.reduce((s, r) => s + r.amount, 0);

  if (storeLoading || isLoading) return <ReportSkeleton />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payment Report"
        description="Sales grouped by payment mode (Cash/COD, Wallet, Razorpay/UPI)."
        actions={<ReportExport title="Payment Report" name="payment-report" rows={table.allRows} columns={paymentsExport} />}
      />
      <StatsGrid columns={3}>
        <CompactStatCard title="Payment modes" value={rows.length} icon={CreditCard} />
        <CompactStatCard title="Orders counted" value={orders.length} icon={ShoppingCart} />
        <CompactStatCard title="Total billed" value={inr(totalAmount)} icon={CreditCard} />
      </StatsGrid>
      <DataTable
        title="By payment mode"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.method}
        search={table.state.search}
        onSearchChange={table.setSearch}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No payment data yet."
      />
    </div>
  );
}

// ------------------------------------------------------------------- BV

const isBvCredit = (row: StoreBvLedgerDto) => row.entryTypeText === "AdminCredit" || row.entryTypeText === "ProductRefund";
const splitWords = (value: string) => value.replace(/([a-z])([A-Z])/g, "$1 $2");

const bvExport: ExportColumn<StoreBvLedgerDto>[] = [
  { header: "Date", value: (r) => new Date(r.createdAt).toLocaleString("en-IN"), width: 22 },
  { header: "Type", value: (r) => splitWords(r.entryTypeText), width: 18 },
  { header: "BV", value: (r) => (isBvCredit(r) ? 1 : -1) * Number(r.amount), numeric: true, width: 12 },
  { header: "Balance after", value: (r) => Number(r.balanceAfter), numeric: true, width: 14 },
  { header: "Details", value: (r) => [r.description, r.productName].filter(Boolean).join(" - "), width: 40 },
];

export function BvReport() {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data, isLoading } = useStoreBvHistory(storeId);
  const balance = data?.balance;
  const entries = data?.entries ?? [];

  const table = useDataTable<StoreBvLedgerDto>({
    data: entries,
    searchKeys: ["description", "entryTypeText", "productName"],
  });

  const columns: DataTableColumn<StoreBvLedgerDto>[] = [
    {
      id: "createdAt",
      header: "Date",
      accessorKey: "createdAt",
      sortable: true,
      mobilePriority: 1,
      cell: (row) => new Date(row.createdAt).toLocaleString("en-IN"),
    },
    {
      id: "type",
      header: "Type",
      mobileLabel: "Type",
      mobilePriority: 2,
      cell: (row) => <Badge variant="outline">{splitWords(row.entryTypeText)}</Badge>,
    },
    {
      id: "amount",
      header: "BV",
      align: "right",
      mobileLabel: "BV",
      mobilePriority: 3,
      cell: (row) => (
        <span className={isBvCredit(row) ? "font-semibold text-success" : "font-semibold text-destructive"}>
          {isBvCredit(row) ? "+" : "-"}
          {formatBv(Number(row.amount))}
        </span>
      ),
    },
    {
      id: "balanceAfter",
      header: "Balance after",
      accessorKey: "balanceAfter",
      align: "right",
      mobileLabel: "Balance",
      mobilePriority: 4,
      cell: (row) => formatBv(Number(row.balanceAfter)),
    },
    {
      id: "details",
      header: "Details",
      mobileLabel: "Details",
      mobilePriority: 5,
      cell: (row) => (
        <div className="text-sm">
          <p>{row.description}</p>
          {row.productName ? <p className="text-xs text-muted-foreground">{row.productName}</p> : null}
        </div>
      ),
    },
  ];

  if (storeLoading || isLoading) return <ReportSkeleton />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="BV Report"
        description="Store BV wallet: received from admin, allocated to products, and refunds."
        actions={<ReportExport title="BV Report" name="bv-report" rows={table.allRows} columns={bvExport} />}
      />
      <StatsGrid columns={3}>
        <CompactStatCard title="Available BV" value={formatBv(Number(balance?.availableBv ?? 0))} icon={Layers} />
        <CompactStatCard title="Total received" value={formatBv(Number(balance?.totalBvReceived ?? 0))} icon={Layers} />
        <CompactStatCard title="Allocated" value={formatBv(Number(balance?.totalBvAllocated ?? 0))} icon={Layers} />
      </StatsGrid>
      <DataTable
        title="BV ledger"
        columns={columns}
        data={table.rows}
        getRowId={(row) => String(row.id)}
        search={table.state.search}
        onSearchChange={table.setSearch}
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No BV activity yet."
      />
    </div>
  );
}

// ------------------------------------------------------------- Products

const productsExport: ExportColumn<StoreProductRow>[] = [
  { header: "Product", value: (r) => r.name, width: 30 },
  { header: "Category", value: (r) => r.category, width: 20 },
  { header: "Status", value: (r) => r.status.replace("_", " "), width: 14 },
  { header: "Price (INR)", value: (r) => Number(r.price), numeric: true, width: 12 },
  { header: "BV", value: (r) => Number(r.businessVolume ?? 0), numeric: true, width: 10 },
  { header: "Stock", value: (r) => (r.stock == null ? "" : r.stock), width: 10 },
];

export function ProductsReport() {
  const { storeId, isLoading: storeLoading } = useStoreId();
  const { data, isLoading } = useStoreCatalog(storeId);
  const products = data?.items ?? [];

  const table = useDataTable<StoreProductRow>({ data: products, searchKeys: ["name", "category"] });

  const active = products.filter((p) => p.status === "active").length;
  const lowStock = products.filter((p) => p.stock != null && p.stock <= 5).length;
  const totalBv = Number(products.reduce((s, p) => s + (p.businessVolume ?? 0), 0).toFixed(2));

  const columns: DataTableColumn<StoreProductRow>[] = [
    {
      id: "name",
      header: "Product",
      accessorKey: "name",
      sortable: true,
      mobilePriority: 1,
      cell: (row) => (
        <div>
          <p className="font-semibold">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.category}</p>
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      mobileLabel: "Status",
      mobilePriority: 2,
      cell: (row) => <Badge variant="outline" className="capitalize">{row.status.replace("_", " ")}</Badge>,
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      align: "right",
      sortable: true,
      mobileLabel: "Price",
      mobilePriority: 3,
      cell: (row) => `₹${row.price}`,
    },
    {
      id: "bv",
      header: "BV",
      align: "right",
      mobileLabel: "BV",
      mobilePriority: 4,
      cell: (row) => formatBv(Number(row.businessVolume ?? 0)),
    },
    {
      id: "stock",
      header: "Stock",
      accessorKey: "stock",
      align: "right",
      sortable: true,
      mobileLabel: "Stock",
      mobilePriority: 5,
      cell: (row) => (row.stock == null ? "—" : row.stock),
    },
  ];

  if (storeLoading || isLoading) return <ReportSkeleton />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Product Report"
        description="Catalog snapshot: status, price, product BV, and stock."
        actions={<ReportExport title="Product Report" name="product-report" rows={table.allRows} columns={productsExport} />}
      />
      <StatsGrid columns={4}>
        <CompactStatCard title="Products" value={products.length} icon={Package} />
        <CompactStatCard title="Active" value={active} icon={Package} />
        <CompactStatCard title="Low stock (≤5)" value={lowStock} icon={Package} />
        <CompactStatCard title="Catalog BV sum" value={formatBv(totalBv)} icon={Layers} />
      </StatsGrid>
      <DataTable
        title="Products"
        columns={columns}
        data={table.rows}
        getRowId={(row) => row.id}
        search={table.state.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Search products..."
        sort={table.state.sort}
        onSortChange={table.toggleSort}
        pagination={table.pagination}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        emptyMessage="No products yet."
      />
    </div>
  );
}
